import React from "react";
import { Box, BoxModel, Editor, TLDefaultHorizontalAlignStyle, TLDefaultVerticalAlignStyle } from "@tldraw/editor";
import { useDefaultColorTheme } from "@tldraw/tldraw";
import { getLegacyOffsetX } from "./text-helper";

export function SvgTextLabel({
  fontSize,
  font,
  align,
  verticalAlign,
  text,
  labelColor,
  bounds,
  padding = 16,
  stroke = true,
  editor,
  fontWeight,
  italic,
  lineHeight,
}: {
  fontSize: number;
  font: string;
  // fill?: TLDefaultFillStyle
  align: TLDefaultHorizontalAlignStyle;
  verticalAlign: TLDefaultVerticalAlignStyle;
  wrap?: boolean;
  text: string;
  labelColor: string;
  bounds: Box;
  padding?: number;
  stroke?: boolean;
  editor: any;
  fontWeight?: number;
  italic?: string;
  lineHeight: number;
}) {
  const theme = useDefaultColorTheme();

  function getFontWeightString(fontWeight: number): string {
    switch (fontWeight) {
      case 300:
        return "light";
      case 400:
        return "normal";
      case 600:
        return "semi-bold";
      case 700:
        return "bold";
      default:
        return "normal";
    }
  }

  const opts = {
    fontSize,
    fontFamily: `"${font}", -apple-system, BlinkMacSystemFont, sans-serif`,
    textAlign: align,
    verticalTextAlign: verticalAlign,
    width: Math.ceil(bounds.width),
    height: Math.ceil(bounds.height),
    padding,
    lineHeight,
    fontStyle: italic ? "italic" : "normal",
    fontWeight: getFontWeightString(fontWeight ?? 400),
    overflow: "wrap" as const,
    offsetX: 0,
    offsetY: 0,
    fill: labelColor,
    stroke: undefined as string | undefined,
    strokeWidth: undefined as number | undefined,
  };

  const spans = editor.textMeasure.measureTextSpans(text, opts);
  const offsetX = getLegacyOffsetX(align, padding, spans, bounds.width);
  if (offsetX) {
    opts.offsetX = offsetX;
  }

  opts.offsetX += bounds.x;
  opts.offsetY += bounds.y;

  const mainSpans = createTextJsxFromSpans(editor, spans, opts);

  let outlineSpans: React.ReactNode = null;
  if (stroke) {
    opts.fill = theme.background;
    opts.stroke = theme.background;
    opts.strokeWidth = 2;
    outlineSpans = createTextJsxFromSpans(editor, spans, opts);
  }

  return (
    <>
      {outlineSpans}
      {mainSpans}
    </>
  );
}

export function createTextJsxFromSpans(
  editor: Editor,
  spans: { text: string; box: BoxModel }[],
  opts: {
    fontSize: number;
    fontFamily: string;
    textAlign: TLDefaultHorizontalAlignStyle;
    verticalTextAlign: TLDefaultVerticalAlignStyle;
    fontWeight: string;
    fontStyle: string;
    width: number;
    height: number;
    stroke?: string;
    strokeWidth?: number;
    fill?: string;
    padding?: number;
    offsetX?: number;
    offsetY?: number;
  }
) {
  const { padding = 0 } = opts;
  if (spans.length === 0) return null;

  const bounds = Box.From(spans[0].box);
  for (const { box } of spans) {
    bounds.union(box);
  }

  const offsetX = padding + (opts.offsetX ?? 0);
  const offsetY =
    (opts.offsetY ?? 0) +
    opts.fontSize / 2 +
    (opts.verticalTextAlign === "start"
      ? padding
      : opts.verticalTextAlign === "end"
      ? opts.height - padding - bounds.height
      : (Math.ceil(opts.height) - bounds.height) / 2);

  // Create text span elements for each word
  let currentLineTop: number | null = null;
  const children: React.ReactNode[] = [];
  for (const { text, box } of spans) {
    // if we broke a line, add a line break span. This helps tools like
    // figma import our exported svg correctly
    const didBreakLine = currentLineTop !== null && box.y > currentLineTop;
    if (didBreakLine) {
      children.push(
        <tspan key={children.length} alignmentBaseline="mathematical" x={offsetX} y={box.y + offsetY}>
          {"\n"}
        </tspan>
      );
    }

    children.push(
      <tspan
        key={children.length}
        alignmentBaseline="mathematical"
        x={box.x + offsetX}
        y={box.y + offsetY}
        // N.B. This property, while discouraged ("intended for Document Type Definition (DTD) designers")
        // is necessary for ensuring correct mixed RTL/LTR behavior when exporting SVGs.
        unicodeBidi="plaintext"
      >
        {correctSpacesToNbsp(text)}
      </tspan>
    );

    currentLineTop = box.y;
  }

  return (
    <text
      fontSize={opts.fontSize}
      fontFamily={opts.fontFamily}
      fontStyle={opts.fontStyle}
      dominantBaseline="mathematical"
      alignmentBaseline="mathematical"
      stroke={opts.stroke}
      strokeWidth={opts.strokeWidth}
      fill={opts.fill}
    >
      {children}
    </text>
  );
}

function correctSpacesToNbsp(input: string) {
  return input.replace(/\s/g, "\xa0");
}
